import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import { setAchievements, setPartners, setTeam, setLoading, setError } from '../features/home/homeSlice';
import { fetchHomeData } from '../features/home/homeAPI';
import NavbarHome from '../features/home/components/NavbarHome'
import Footer from '../features/components/Footer';

const Home = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { achievements, partners, team, loading, error } = useSelector((state) => state.home);

  useEffect(() => {
    const loadHomeData = async () => {
      try {
        dispatch(setLoading(true));
        const data = await fetchHomeData();
        dispatch(setAchievements(data.achievements));
        dispatch(setPartners(data.partners));
        dispatch(setTeam(data.team));
      } catch (err) {
        dispatch(setError(err.message));
      } finally {
        dispatch(setLoading(false));
      }
    };

    loadHomeData();
  }, [dispatch]);

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    else{
      const element = document.getElementById("hero");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-600"></div>
    </div>
  );
  
  if (error) return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="text-red-500 text-center p-4 bg-red-50 rounded-lg">
        <p className="text-lg font-semibold">Error loading content</p>
        <p className="text-sm">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <section id="nav">
        <NavbarHome />
      </section>
      <div className="pt-16"> {/* Add padding-top to account for fixed navbar */}
        {/* Hero Section */}
        <section id="hero" className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 text-white h-[calc(100vh-4rem)] flex items-center">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Left side - Informative Section */}
              <div className="text-center lg:text-left">
                <h3 className="text-3xl sm:text-4xl font-extrabold mb-6">
                  Your Golden Gateway to Success
                </h3>
                <p className="text-lg text-white/90 mb-6 leading-relaxed">
                  At SwarnBhandar, we don’t just help trade gold; we redefine it by smartly connecting wholesalers and retailers. Join us to experience a seamless, secure, and smart way to manage your gold business.
                </p>
              </div>
              


              {/* Center - Main Title */}
                      <div className="text-center">
                      <h3 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-4">
                        Shine Brighter, Trade Smarter
                      </h3>
                      <p className="text-lg sm:text-xl text-white/90 mb-6">
                        Your trusted partner for effortless gold jewellery wholesale operations.
                      </p>
                      <p className="text-lg sm:text-xl text-white/90 mb-6">
                      Ready to elevate your Business?
                      </p>
                      <div className="flex justify-center space-x-4">
                        <Link
                        to="/signup"
                        className="inline-block bg-white text-yellow-600 px-6 py-3 rounded-full font-semibold hover:bg-yellow-100 transition-all duration-200 transform hover:scale-105 shadow-lg"
                        >
                        Sign Up Now
                        </Link>
                        <Link
                        to="/login"
                        className="inline-block bg-yellow-700 text-white px-6 py-3 rounded-full font-semibold hover:bg-yellow-800 transition-all duration-200 transform hover:scale-105 shadow-lg"
                        >
                        Login
                        </Link>
                      </div>
                      </div>
              <div className="hidden lg:block">
                <img
                  src="/gold_images/000011.jpg" // Ensure this image is placed in the 'public/gold_images' folder
                    alt="Gold Jewelry"
                    className="rounded-lg shadow-2xl transform hover:scale-105 transition-all duration-200"
                    />
                    </div>
                  </div>
                  </div>
                  </section>

                  {/* About Us Section */}
                  <section id="about" className="bg-gradient-to-r from-yellow-100 to-yellow-300 py-16">
                  <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl sm:text-5xl font-extrabold mb-8 text-yellow-800">
                      Transforming the Gold Industry
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-700 mb-6 leading-relaxed">
                      At SwarnBhandar, we are revolutionizing the gold jewellery wholesale process with cutting-edge technology and innovative solutions.
                    </p>
                    <p className="text-lg sm:text-xl text-gray-700 leading-relaxed">
                      Our platform connects businesses seamlessly, streamlines operations, and empowers growth in the competitive gold market.
                    </p>
                    </div>
                    <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-8">
                    {[
                      {
                      icon: "💎",
                      title: "Innovative Tools",
                      description: "Leverage state-of-the-art features to manage inventory, track orders, and optimize your business operations.",
                      },
                      {
                      icon: "🤝",
                      title: "Trusted Network",
                      description: "Join a community of verified partners by seamlessly connecting with them .",
                      },
                    ].map((item, index) => (
                      <div
                      key={index}
                      className="bg-white p-8 rounded-lg shadow-lg text-center transform hover:scale-105 transition-all duration-200"
                      >
                      <div className="text-4xl mb-4">{item.icon}</div>
                      <h3 className="text-2xl font-semibold mb-4 text-yellow-900">{item.title}</h3>
                      <p className="text-gray-800 text-base">{item.description}</p>
                      </div>
                    ))}
                    </div>
                  </div>
                  </section>

                  {/* Why Choose Us Section */}
        <section id="why-choose-us" className="bg-white py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12 text-yellow-800">
              Why SwarnBhandar?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: "Verified Partners",
                  description: "Work with trusted suppliers and buyers for secure transactions.",
                },
                {
                  title: "Smart Insights",
                  description: "Access data-driven tools to make informed business decisions.",
                },
                {
                  title: "Effortless Operations",
                  description: "Simplify your workflow with our user-friendly platform.",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-yellow-200 to-yellow-400 p-8 rounded-lg shadow-lg text-center transform hover:scale-105 transition-all duration-200"
                >
                  <h3 className="text-2xl font-semibold mb-4 text-yellow-900">{item.title}</h3>
                  <p className="text-gray-800 text-base">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Team */}
        <section id = "team" className="bg-gray-50 h-[calc(100vh-4rem)] flex items-center">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-12">
              Our Team
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-center">
              <div className="bg-white p-6 rounded-lg shadow-md text-center transform hover:scale-105 transition-all duration-200">
                <img 
                  src={"/gold_images/Darpan.jpeg"} 
                  alt="Darpan Balar" 
                  className="w-48 h-48 sm:w-64 sm:h-64 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg sm:text-xl font-semibold mb-1">
                  Darpan Balar
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Co-Founder
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center transform hover:scale-105 transition-all duration-200">
                <img 
                  src="../public/gold_images/Charchit.jpeg" 
                  alt="Charchit Paharia" 
                  className="w-48 h-48 sm:w-64 sm:h-64 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg sm:text-xl font-semibold mb-1">
                  Charchit Paharia
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Co-Founder
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <section id="contact">
          <Footer />
        </section>
      </div>
    </div>
  );
};

export default Home; 